import type { CSSProperties } from 'react'
import { cx } from '../../cx'
import styles from './Logo.module.css'

/*
 * Znak k1p4 wg księgi znaku 1.0. Zakazy z sekcji 06 (obrót, zniekształcenie,
 * kolory spoza palety, cienie/gradienty) są wymuszone brakiem propsów —
 * komponent nie przyjmuje rotate/shadow/gradient/custom color.
 */

export type LogoVariant = 'primary' | 'onLight' | 'onDark' | 'mono' | 'avatar'

export interface LogoProps {
  /** Bok kafelka. Księga: znak działa od 16 px wzwyż. */
  size: number
  variant?: LogoVariant
  /** Wordmark „k1p4." — kropka zawsze w kolorze --k1-pink. */
  withWordmark?: boolean
  /** Tagline lockupu: „KADRY & PŁACE" (Roboto Mono). */
  withTagline?: boolean
  /**
   * Nadpisanie tagline'u dla lockupów kontekstowych — portal onboardingu ma
   * pod znakiem „ONBOARDING" (spec 04). Znak i wordmark zostają bez zmian.
   */
  tagline?: string
  className?: string
}

export const LOGO_MIN_SIZE = 16
export const LOGO_CHEVRON_PATH = 'M5 13l7-6 7 6'
export const LOGO_TAGLINE = 'KADRY & PŁACE'

/** Księga, sekcja 07: przy małych rozmiarach kreska grubieje do 3–3,2. */
export function logoStrokeWidth(size: number): number {
  if (size >= 48) return 2.6
  if (size >= 32) return 3
  return 3.2
}

export function Logo({
  size,
  variant = 'primary',
  withWordmark = false,
  withTagline = false,
  tagline = LOGO_TAGLINE,
  className,
}: LogoProps) {
  if (!Number.isFinite(size) || size < LOGO_MIN_SIZE) {
    throw new Error(
      `Logo: minimalny rozmiar znaku to ${LOGO_MIN_SIZE} px (księga znaku, sekcja 07)`,
    )
  }

  const strokeWidth = logoStrokeWidth(size)
  const chevronSize = Math.round(size * 0.48)
  const tileStyle: CSSProperties = {
    width: size,
    height: size,
    /* wierzchołek chevrona w górnej tercji pola */
    paddingTop: Math.round(size * 0.28),
  }
  if (variant !== 'avatar') {
    /* radius proporcjonalny ~27% boku; awatar to okrąg (radius-pill w CSS) */
    tileStyle.borderRadius = Math.round(size * 0.27)
  }

  const showText = withWordmark || withTagline

  return (
    <span className={cx(styles.root, className)} role="img" aria-label="k1p4">
      <span className={cx(styles.tile, styles[variant])} style={tileStyle}>
        <svg
          viewBox="0 0 24 24"
          width={chevronSize}
          height={chevronSize}
          fill="none"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d={LOGO_CHEVRON_PATH} />
        </svg>
      </span>
      {showText ? (
        <span className={styles.text}>
          {withWordmark ? (
            <span className={styles.wordmark} style={{ fontSize: Math.round(size * 0.54) }}>
              k1p4<span className={styles.dot}>.</span>
            </span>
          ) : null}
          {withTagline ? <span className={styles.tagline}>{tagline}</span> : null}
        </span>
      ) : null}
    </span>
  )
}
